/**
 * Routing an imported file into the open **content folder** — the scene's, or
 * the library asset's (CONTENT_MODEL_SPEC §2.6, first row).
 *
 * Imports upload **eagerly**, and that is what makes a save cheap: by the time
 * the document names `assets/<slug>.<hash16>.<ext>`, the folder already holds
 * those bytes, so saving is one JSON POST however large the scene is (§2.2).
 *
 * The file is handed to the viewer under the folder file's own name. That is
 * the whole trick: `TraceScene` keys a blob import at `assets/<file name>` and
 * writes that as the object's `src`, so renaming the blob to the server's hash
 * makes the viewer author exactly the uri the server stored it under — there
 * is no rewrite pass at save time, and nothing transient (a `blob:` URL) ever
 * reaches a document. The display name travels separately so the user still
 * sees "Chair", not a hash.
 *
 * Which folder is a routing decision, and only the endpoint differs: a CMS
 * scene's `POST /scenes/{id}/files`, or the open asset's twin
 * `POST /assets/{id}/files` (§2.4). A scene session wins when both somehow
 * exist — the scene is what the viewer is actually showing, and a scene's
 * folder file may not live in an asset folder (§3.2 runs the other way).
 * With neither, nothing is uploaded and the import happens in memory, exactly
 * as a standalone editor session always has.
 */

import type { TraceScene } from 'trace-viewer/scene';
import { getActiveCmsManager } from './active-manager';
import { getAssetFolderSession } from './asset-folder';
import { registerFolderFileUrl } from './scene-folder';
import { EDITOR_SCENE_EXTENSIONS, fileExtension } from '../scene-formats';

export interface FolderImport {
    /** The blob to hand `viewer.import`, named as the folder file. */
    file: File;
    /** Object name to pass alongside it, or undefined to keep the default. */
    displayName: string | undefined;
    /** The relative uri the scene folder stored it under, when it was uploaded. */
    uri: string | undefined;
    /**
     * The name the user's file actually had, to be recorded as `files[k].name`
     * (spec §2.1, §3.6) — see {@link registerFolderFileName}. Undefined when
     * nothing was uploaded, because then the viewer already holds the right
     * name: it was handed the file under its own name.
     */
    originalName: string | undefined;
}

/**
 * The folder an import lands in: where to put the bytes, and how to make the
 * uri that comes back resolve for the rest of the session.
 *
 * The two implementations differ only in the endpoint and in who owns the
 * bookkeeping — `CmsManager` for a scene, the module-level session for an
 * asset — so the import itself is written once.
 */
interface FolderTarget {
    /** The word for this folder in a log line. */
    kind: 'scene' | 'asset';
    /** Put the bytes in the folder; answer the relative uri they are stored under. */
    upload(file: File): Promise<string>;
    /**
     * Register the absolute URL that uri resolves to, so the reference
     * survives the in-memory blob (a `clearScene`, a behavior reload).
     */
    register(traceScene: TraceScene, uri: string): void;
}

/** The open folder, scene first. Null when the editor is on a standalone scene. */
function openFolderTarget(): FolderTarget | null {
    const cms = getActiveCmsManager();
    if (cms) {
        return {
            kind: 'scene',
            upload: file => cms.importFile(file, file.name),
            register: (traceScene, uri) => cms.registerImportedFile(traceScene, uri),
        };
    }

    const session = getAssetFolderSession();
    if (session) {
        return {
            kind: 'asset',
            upload: async (file) => {
                const { uri } = await session.uploader.uploadSceneFile(session.assetId, file, file.name);
                return uri;
            },
            register: (traceScene, uri) => registerFolderFileUrl(traceScene.resources, session.baseUrl, uri),
        };
    }

    return null;
}

/**
 * Upload `file` into the open content folder, if there is one.
 *
 * Scene formats are skipped: a dropped `.trace`/`.glz` is unpacked into
 * this scene's own objects, so its bytes never become a folder file. Its
 * *contents* do, and they go up with the next save.
 *
 * Best-effort by design. If the upload fails the import still happens against
 * in-memory bytes and the save-time upload pass catches it — an editor that
 * refuses to place a model because a POST failed would be worse than one that
 * places it and uploads late.
 */
export async function importIntoSceneFolder(file: File, traceScene: TraceScene | null | undefined): Promise<FolderImport> {
    const unchanged: FolderImport = { file, displayName: undefined, uri: undefined, originalName: undefined };

    if (!traceScene || EDITOR_SCENE_EXTENSIONS.has(fileExtension(file.name))) return unchanged;
    const target = openFolderTarget();
    if (!target) return unchanged;

    const tag = target.kind === 'asset' ? '[AssetFolder]' : '[SceneFolder]';
    try {
        const uri = await target.upload(file);
        target.register(traceScene, uri);
        const fileName = uri.slice(uri.lastIndexOf('/') + 1);
        const displayName = file.name.replace(/\.[^/.]+$/, '');
        console.log(`${tag} Imported ${file.name} into the ${target.kind} folder as ${uri}`);
        return { file: new File([file], fileName, { type: file.type }), displayName, uri, originalName: file.name };
    } catch (error) {
        console.warn(
            `${tag} Could not import ${file.name} into the ${target.kind} folder; `
            + `keeping it in memory until the next save:`,
            error,
        );
        return unchanged;
    }
}

/**
 * The blob-import variant, for callers that synthesize bytes rather than
 * receive a `File` (image and model generation).
 */
export async function importBlobIntoSceneFolder(
    blob: Blob,
    fileName: string,
    traceScene: TraceScene | null | undefined,
): Promise<FolderImport> {
    const file = blob instanceof File && blob.name === fileName
        ? blob
        : new File([blob], fileName, { type: blob.type });
    return importIntoSceneFolder(file, traceScene);
}

/**
 * Record the file's own name against the folder uri, so the next save writes
 * `files[k].name` (spec §2.1, §3.6) and the editor can label a folder file
 * with something other than its hash.
 *
 * **Must run after `viewer.import`, not before.** The rename above is the
 * whole trick that lets an object be authored with its final `src`, and the
 * viewer records a name of its own from the file name it is handed — which by
 * then is the server's hash. This puts the human name back over it. A hash is
 * never a name (trace-viewer#814), so when nothing was uploaded there is
 * nothing to correct and this is a no-op.
 */
export function registerFolderFileName(
    traceScene: TraceScene | null | undefined,
    imported: FolderImport,
): void {
    if (!traceScene || !imported.uri || !imported.originalName) return;
    traceScene.resources.registerAssetName(imported.uri, imported.originalName);
}
